import React, { createContext, useContext, useState, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemeMode;
}

export const ThemeContextProvider = ({
  children,
  initialTheme = 'light',
}: ThemeProviderProps): React.ReactElement => {
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('selectedTheme');
    return stored === 'dark' || stored === 'light' ? stored : initialTheme;
  });

  const updateTheme = (newTheme: ThemeMode): void => {
    setTheme(newTheme);
    localStorage.setItem('selectedTheme', newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};
